import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDisaggregatedNutrition, useMeals } from '@/hooks/queries';
import { useNutrientTimingPrefs } from '@/hooks/useNutrientTimingPrefs';
import type { Meal } from '@/api/meals';
import { NutrientTimingChart } from '@/components/charts/NutrientTimingChart';
import { NutrientTimingSelect } from '@/components/charts/NutrientTimingSelect';
import { MacroDistributionChart } from '@/components/charts/MacroDistributionChart';

/**
 * Renders nothing — warms the React Query cache for the Summary distribution
 * graphs so they're ready when the clinician opens Summary. Mounted at the
 * PatientShell level with a read-only URL range (so it never writes params),
 * gated by the caller on a present range. The heavy part is the disaggregated
 * per-meal fan-out; with the long staleTime/gcTime on those queries it fetches
 * once per patient session and stays warm across tab hops.
 */
export const NutrientDistributionPrefetch = ({
  patientId,
  range,
}: {
  patientId: number;
  range: { from: string; to: string };
}) => {
  const meals = useMeals(patientId, range);
  useDisaggregatedNutrition(patientId, meals.data ?? []);
  return null;
};

/**
 * Summary-page reproduction of the PDF report's Calorie & macronutrient
 * distribution section, as two inline cards (side-by-side at md+, like the
 * Average Plate + Med Score row):
 *   - "Nutrient timing" → time-of-day distribution with a nutrient selector
 *   - "Macronutrient distribution" → the 4-bar AMDR chart
 *
 * Both run off disaggregated per-meal nutrition (composites split into
 * components) so the numbers match the PDF report; the timing chart buckets by
 * each meal's time, the macro chart aggregates the whole range.
 */
export const NutrientDistributionCards = ({
  patientId,
  meals,
}: {
  patientId: number;
  meals: Meal[];
}) => {
  const { t } = useTranslation();
  const [nutrientKeys, setNutrientKeys] = useNutrientTimingPrefs();

  const { rows, isLoading } = useDisaggregatedNutrition(patientId, meals);

  return (
    <div className="cq-pair-grid">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
          <CardTitle className="text-base">{t('Nutrient timing')}</CardTitle>
          <NutrientTimingSelect selected={nutrientKeys} onChange={setNutrientKeys} />
        </CardHeader>
        <CardContent>
          <NutrientTimingChart
            meals={meals}
            rows={rows}
            isLoading={isLoading}
            nutrientKeys={nutrientKeys}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('Macronutrient distribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          <MacroDistributionChart rows={rows} isLoading={isLoading} />
        </CardContent>
      </Card>
    </div>
  );
};
